import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card, CardBody, CardTitle, Row, Col, Table, Badge, Button, Input, Label, Form, Alert, Spinner,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { ApiProxyGuard } from '../../components/ApiProxyGuard';
import { ApiHealthStrip } from '../../components/ApiHealthStrip';
import { useHasPermission } from '../../rbac/useHasPermission';
import { valisightFetch, isValisightProxyEnabled } from './valisightClient';
import {
  SEVERITIES, CHANNELS, MATCH_TYPES, channelConfigFields, summarizeRule,
} from './alertRulesLogic';
import {
  useValisightAlertRules, useCreateAlertRule, useToggleAlertRule,
  useTestAlertRule, useDeleteAlertRule,
} from './useValisightAlertRules';

/**
 * #261 P3 / #113 — Alert-Regeln real gegen valisight verdrahtet (kein Fixture).
 * Status/Health über den echten /healthz der valisight-Instanz, Regel-CRUD über
 * /v1/alert-rules. Fehlende Admin-Flächen (Ingest-Volumen-Aggregat, Umsatz je
 * Tarif) existieren als Backend-Route noch nicht und sind daher bewusst NICHT
 * verdrahtet (siehe Issue-Bericht).
 */

const SEV_COLORS = { info: 'secondary', warn: 'warning', error: 'danger', critical: 'danger' };
const EMPTY_FORM = {
  name: '', channel: 'webhook', minSeverity: 'error',
  url: '', botToken: '', chatId: '', to: '',
  matchType: '', pattern: '', cooldownSecs: '', tenantId: '', sourceId: '',
};

// Eingabefelder je Kanal — dieselbe Quelle wie die Validierung (channelConfigFields).
const FIELD_LABEL = {
  url: 'Incoming-Webhook-URL', botToken: 'Bot-Token', chatId: 'Chat-ID', to: 'Empfänger-E-Mail',
};

function ProxyGuard({ children }) {
  return (
    <ApiProxyGuard enabled={isValisightProxyEnabled()} label="ValiSight" envVar="VALISIGHT_API_URL" proxyPath="/proxy/valisight">
      {children}
    </ApiProxyGuard>
  );
}

function RuleRow({ rule, canWrite, tenantId, onToggle, onTest, onDelete, busy }) {
  const s = summarizeRule(rule);
  return (
    <tr>
      <td>
        {s.name}
        {!s.enabled && <Badge color="secondary" pill className="ms-1">aus</Badge>}
        <div className="small text-muted">Cooldown {s.cooldownMin} min · Treffer: {s.match}</div>
      </td>
      <td><Badge color={SEV_COLORS[s.minSeverity] ?? 'secondary'} pill>{s.minSeverity}</Badge></td>
      <td className="small">{s.channel}<div className="text-muted font-monospace text-truncate" style={{ maxWidth: 220 }} title={s.target}>{s.target}</div></td>
      <td className="text-end">
        {canWrite && (
          <>
            <Button size="sm" color="link" className="p-0 me-2" disabled={busy}
              onClick={() => onToggle({ id: s.id, enabled: !s.enabled, tenantId })}>
              {s.enabled ? 'deaktivieren' : 'aktivieren'}
            </Button>
            <Button size="sm" color="link" className="p-0 me-2" disabled={busy}
              onClick={() => onTest({ id: s.id, tenantId })}>testen</Button>
            <Button size="sm" color="link" className="p-0 text-danger" disabled={busy}
              onClick={() => { if (window.confirm(`Regel „${s.name}" löschen?`)) onDelete({ id: s.id, tenantId }); }}>löschen</Button>
          </>
        )}
      </td>
    </tr>
  );
}

export function ValisightAlertRulesPage() {
  const canWrite = useHasPermission({ perms: ['integrations.write'] });
  const [scope, setScope] = useState({ tenantId: '', sourceId: '' });
  const [form, setForm] = useState(EMPTY_FORM);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const health = useQuery({
    queryKey: ['vs-health'], queryFn: () => valisightFetch('/healthz'),
    refetchInterval: 30_000, retry: false, enabled: isValisightProxyEnabled(),
  });
  const rules = useValisightAlertRules(scope);
  const create = useCreateAlertRule();
  const toggle = useToggleAlertRule();
  const test = useTestAlertRule();
  const del = useDeleteAlertRule();
  const busy = toggle.isPending || test.isPending || del.isPending;

  const items = rules.data?.rules ?? [];
  const configFields = channelConfigFields(form.channel);

  const submit = async (e) => {
    e.preventDefault();
    // tenantId/sourceId aus dem Filter als Default in die neue Regel ziehen.
    await create.mutateAsync({ ...form, tenantId: form.tenantId || scope.tenantId, sourceId: form.sourceId || scope.sourceId });
    setForm(EMPTY_FORM);
  };

  return (
    <div>
      <PageHeader title="ValiSight — Alert-Regeln"
        subtitle="Log-Alarme je Tenant/Quelle: Kanal, Schweregrad, Muster, Cooldown — real gegen valisight." />
      <ProxyGuard>
        <ApiHealthStrip health={health} />

        <Card className="mb-3"><CardBody>
          <Row className="g-2 align-items-end mb-2">
            <Col md={3}><Label className="small">Filter Tenant-ID (leer = tenant-weit)</Label>
              <Input bsSize="sm" value={scope.tenantId} onChange={(e) => setScope((s) => ({ ...s, tenantId: e.target.value }))} placeholder="z. B. 42" /></Col>
            <Col md={3}><Label className="small">Filter Quelle-ID (optional)</Label>
              <Input bsSize="sm" value={scope.sourceId} onChange={(e) => setScope((s) => ({ ...s, sourceId: e.target.value }))} /></Col>
          </Row>
          <CardTitle tag="h6">Regeln <Badge color="secondary" pill>{items.length}</Badge></CardTitle>
          {rules.isLoading ? <Spinner size="sm" />
            : rules.isError ? <Alert color="danger" className="small">{rules.error?.message}</Alert>
            : !items.length ? <div className="small text-muted">Keine Regeln in diesem Scope.</div>
            : (
              <Table size="sm" responsive hover className="align-middle">
                <thead><tr><th>Regel</th><th style={{ width: 90 }}>ab Severity</th><th>Kanal / Ziel</th><th style={{ width: 220 }} className="text-end">Aktionen</th></tr></thead>
                <tbody>
                  {items.map((r) => (
                    <RuleRow key={r.id} rule={r} canWrite={canWrite} tenantId={scope.tenantId} busy={busy}
                      onToggle={toggle.mutate} onTest={test.mutate} onDelete={del.mutate} />
                  ))}
                </tbody>
              </Table>
            )}
        </CardBody></Card>

        {canWrite && (
          <Card><CardBody>
            <CardTitle tag="h6">Regel anlegen</CardTitle>
            <Form onSubmit={submit}>
              <Row className="g-2">
                <Col md={5}><Label className="small">Name</Label>
                  <Input bsSize="sm" required value={form.name} onChange={set('name')} placeholder="z. B. Prod-DB kritisch" /></Col>
                <Col md={3}><Label className="small">Kanal</Label>
                  <Input type="select" bsSize="sm" value={form.channel} onChange={set('channel')}>
                    {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </Input></Col>
                <Col md={2}><Label className="small">ab Severity</Label>
                  <Input type="select" bsSize="sm" value={form.minSeverity} onChange={set('minSeverity')}>
                    {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </Input></Col>
                <Col md={2}><Label className="small">Cooldown (s)</Label>
                  <Input bsSize="sm" type="number" min={0} max={86400} value={form.cooldownSecs} onChange={set('cooldownSecs')} placeholder="900" /></Col>

                {configFields.map((field) => (
                  <Col md={field === 'url' || field === 'to' ? 8 : 4} key={field}>
                    <Label className="small">{FIELD_LABEL[field] ?? field}</Label>
                    <Input bsSize="sm" value={form[field]} onChange={set(field)} />
                  </Col>
                ))}

                <Col md={3}><Label className="small">matchType (optional)</Label>
                  <Input type="select" bsSize="sm" value={form.matchType} onChange={set('matchType')}>
                    <option value="">— alle Treffer —</option>
                    {MATCH_TYPES.map((m) => <option key={m} value={m}>{m}</option>)}
                  </Input></Col>
                <Col md={9}><Label className="small">Muster (pattern) — nur mit matchType</Label>
                  <Input bsSize="sm" value={form.pattern} onChange={set('pattern')} disabled={!form.matchType} placeholder="ECONNREFUSED" /></Col>

                <Col md={12}>
                  <Button size="sm" color="primary" type="submit" disabled={create.isPending}>
                    {create.isPending ? 'Speichere …' : 'Regel anlegen'}
                  </Button>
                </Col>
              </Row>
            </Form>
          </CardBody></Card>
        )}
      </ProxyGuard>
    </div>
  );
}
